import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import Box from '@mui/material/Box';
import { Button, Input, Alert } from '../ui';
import { useLogin } from '../../hooks/auth';
import { getUserFriendlyErrorMessage } from '../../lib/errorMessages';
import { Role } from '@lov/shared';
import { apiClient } from '../../lib/api/client';

function getLoginErrorMessage(error: unknown): string {
  // Network errors on the login page are misleading as "check your internet" —
  // the login service may be down, not the user's connection.
  if (
    error !== null &&
    typeof error === 'object' &&
    'isAxiosError' in error &&
    (error as { response?: unknown }).response == null
  ) {
    return 'Login service is temporarily unavailable. Please try again.';
  }
  return getUserFriendlyErrorMessage(error);
}

const loginSchema = z.object({
  email: z.string().trim().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: login, isPending, isError, error } = useLogin();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Show session expired toast if redirected due to expired session
  useEffect(() => {
    if (sessionStorage.getItem('lov_session_expired') === 'true') {
      sessionStorage.removeItem('lov_session_expired');
      toast.error('Your session has expired. Please log in again.');
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    login(data, {
      onSuccess: async (response) => {
        // Set redirecting state to keep button in loading state
        setIsRedirecting(true);

        try {
          // Check if there's a return URL: router state first, then sessionStorage fallback
          const from =
            (location.state as { from?: string })?.from || sessionStorage.getItem('lov_return_to');
          sessionStorage.removeItem('lov_return_to');

          // Role-based redirect: Vendor/Admin always go to dashboard regardless of return URL
          switch (response.user.role) {
            case Role.SUPER_ADMIN:
              navigate('/admin/dashboard', { replace: true });
              break;
            case Role.VENDOR:
            case Role.VENDOR_STAFF:
              // Check if vendor user has a vendor entity
              try {
                const vendorResponse = await apiClient.get<{ id: string } | null>('/vendors/me');
                if (!vendorResponse.data) {
                  // No vendor entity - redirect to home
                  navigate('/', { replace: true });
                } else {
                  // Has vendor entity - go to dashboard
                  navigate('/vendor/dashboard', { replace: true });
                }
              } catch (error) {
                // On error, default to dashboard (it will handle the redirect)
                navigate('/vendor/dashboard', { replace: true });
              }
              break;
            case Role.CUSTOMER:
            default:
              // Customers can use return URL; Vendor/Admin always go to their dashboard
              navigate(from || '/dashboard', { replace: true });
              break;
          }
        } catch (error) {
          // Reset redirecting state on error
          setIsRedirecting(false);
          console.error('Navigation error:', error);
        }
      },
      onError: () => {
        // Reset redirecting state on error
        setIsRedirecting(false);
      },
    });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
    >
      {isError && <Alert variant="error">{getLoginErrorMessage(error)}</Alert>}

      <Input
        label="Email"
        type="email"
        {...register('email')}
        error={errors.email?.message}
        disabled={isPending}
        autoComplete="email"
        fullWidth
      />

      <Input
        label="Password"
        type="password"
        {...register('password')}
        error={errors.password?.message}
        disabled={isPending}
        autoComplete="current-password"
        fullWidth
      />

      <Button type="submit" fullWidth isLoading={isPending || isRedirecting} glow>
        Log In
      </Button>
    </Box>
  );
}
